import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useFormik } from "formik";
import { debounce } from "lodash";

import Api from "lib/api/httpRequestHelper";

import {
  Grid,
  Button,
  Container,
  CircularProgress,
  FormGroup,
  FormLabel,
  OutlinedInput,
  InputAdornment,
} from "@material-ui/core";

import AutoCompleteField from "components/_refactor/fields/autocomplete";

import { IsMobile } from "utils/is-mobile";

import themeColorsDesingSystem from "assets/theme/ds-colors";

const statusOptions = [
  { value: "A", label: "A" },
  { value: "C", label: "C" },
  { value: "E", label: "E" },
  { value: "F", label: "F" },
  { value: "L", label: "L" },
  { value: "P", label: "P" },
  { value: "X", label: "X" },
];

const qualityOptions = [
  { id: "1", value: "1", label: "Primeira" },
  { id: "2", value: "2", label: "Segunda" },
];

function mapLabelOptions(list) {
  if (!Array.isArray(list)) return [];
  return list
    .filter((item) => item != null && String(item).trim() !== "")
    .map((item) => {
      const label = typeof item === "string" ? item.trim() : String(item).trim();
      return { id: label, label };
    });
}

function buildQueryString(params) {
  const parts = Object.entries(params).filter(
    ([, value]) => value != null && value !== "",
  );
  if (parts.length === 0) return "";
  return "?" + parts.map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
}

export default function Filters({
  handleSearch,
  searching,
  disableImportFile,
  idMarca,
}) {
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [collections, setCollections] = useState([]);
  const [commercialNames, setCommercialNames] = useState([]);
  const [loadingFilters, setLoadingFilters] = useState({
    categories: false,
    subcategories: false,
    collections: false,
    commercialNames: false,
    skus: false,
  });
  const [skus, setSkus] = useState([]);
  const commercialLoadSeqRef = useRef(0);
  const idMarcaRef = useRef(idMarca);
  idMarcaRef.current = idMarca;

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      category: null,
      subcategory: null,
      collection: null,
      status: null,
      quality: null,
      commercialName: null,
      sku: [],
      importFile: null,
    },
    onSubmit: (values) => {
      handleSearch({ ...values, initializePreSelection: true });
    },
  });

  const isMobile = IsMobile();

  const handleChange = (key, value) => {
    formik.setFieldValue(key, value);
  };

  const handleGetCategories = async () => {
    try {
      setLoadingFilters((prev) => ({ ...prev, categories: true }));
      const query = buildQueryString({ idMarca });
      const response = await Api.get(
        "product-category",
        "product",
        query,
        true,
      );

      setCategories(
        mapLabelOptions(
          response.Result.ListaCategoria.map((item) => item.Descricao),
        ),
      );
    } catch (error) {
      console.error("Erro ao buscar categorias", error);
    } finally {
      setLoadingFilters((prev) => ({ ...prev, categories: false }));
    }
  };

  const handleGetSubcategories = async (category) => {
    setLoadingFilters((prev) => ({ ...prev, subcategories: true }));
    try {
      const query = buildQueryString({
        categoria: category || undefined,
        idMarca,
      });
      const response = await Api.get(
        "product-subcategory",
        "product",
        query,
        true,
      );
      setSubcategories(
        mapLabelOptions(
          response.Result.ListaSubCategoria.map((item) => item.Descricao),
        ),
      );
    } catch (error) {
      console.error("Erro ao buscar subcategorias", error);
    } finally {
      setLoadingFilters((prev) => ({ ...prev, subcategories: false }));
    }
  };

  const handleGetCollections = async () => {
    setLoadingFilters((prev) => ({ ...prev, collections: true }));
    try {
      const query = buildQueryString({ idMarca });
      const response = await Api.get(
        "product-colecao-list",
        "product",
        query,
        true,
      );

      setCollections(mapLabelOptions(response.Result));
    } catch (error) {
      console.error("Erro ao buscar coleções", error);
    } finally {
      setLoadingFilters((prev) => ({ ...prev, collections: false }));
    }
  };

  const handleGetCommercialNames = useCallback(async (searchTerm = "") => {
    if (!idMarcaRef.current) {
      setCommercialNames([]);
      return;
    }

    const seq = ++commercialLoadSeqRef.current;
    setLoadingFilters((prev) => ({ ...prev, commercialNames: true }));

    try {
      const query = buildQueryString({
        idMarca: idMarcaRef.current,
        filter: searchTerm?.trim() || undefined,
      });
      const response = await Api.get(
        "product-nome-produto-edi-list",
        "product",
        query,
        true,
      );

      if (seq !== commercialLoadSeqRef.current) return;
      setCommercialNames(mapLabelOptions(response?.Result ?? []));
    } catch (error) {
      if (seq !== commercialLoadSeqRef.current) return;
      console.error("Erro ao buscar nomes comerciais", error);
      setCommercialNames([]);
    } finally {
      if (seq === commercialLoadSeqRef.current) {
        setLoadingFilters((prev) => ({ ...prev, commercialNames: false }));
      }
    }
  }, []);

  const debouncedGetCommercialNames = useMemo(
    () =>
      debounce((searchTerm) => {
        handleGetCommercialNames(searchTerm);
      }, 650),
    [handleGetCommercialNames],
  );

  const handleCommercialInputChange = useCallback(
    (_, inputValue, reason) => {
      if (reason !== "input") return;
      debouncedGetCommercialNames(inputValue ?? "");
    },
    [debouncedGetCommercialNames],
  );

  const handleGetSkus = async () => {
    setLoadingFilters((prev) => ({ ...prev, skus: true }));
    try {
      const query = buildQueryString({ idMarca });
      const response = await Api.get(
        "product-ean-list",
        "product",
        query,
        true,
      );
      setSkus(
        mapLabelOptions(response.Result.ListaEan).map((item) => ({
          ...item,
          value: item.label,
        })),
      );
    } catch (error) {
      console.error("Erro ao buscar SKUs", error);
    } finally {
      setLoadingFilters((prev) => ({ ...prev, skus: false }));
    }
  };

  useEffect(() => {
    handleGetCategories();
    handleGetCollections();
    handleGetSkus();
  }, [idMarca]);

  useEffect(() => {
    return () => {
      debouncedGetCommercialNames.cancel();
    };
  }, [debouncedGetCommercialNames]);

  useEffect(() => {
    if (formik.values.category?.label) {
      handleGetSubcategories(formik.values.category.label);
      return;
    }

    setSubcategories([]);
  }, [formik.values.category]);

  return (
    <form onSubmit={formik.handleSubmit}>
      <Container maxWidth="100%" style={{ padding: 0, marginTop: "10px" }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <AutoCompleteField
              options={categories}
              label="Categoria"
              optionLabelKey="label"
              optionIdKey="id"
              onChange={(option) => handleChange("category", option)}
              value={formik.values.category}
              disabled={searching || loadingFilters.categories}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <AutoCompleteField
              options={subcategories}
              label="Subcategoria"
              optionLabelKey="label"
              optionIdKey="id"
              onChange={(option) => handleChange("subcategory", option)}
              value={formik.values.subcategory}
              disabled={searching || loadingFilters.subcategories}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <AutoCompleteField
              options={collections}
              label="Coleção"
              optionLabelKey="label"
              optionIdKey="id"
              onChange={(option) => handleChange("collection", option)}
              value={formik.values.collection}
              disabled={searching || loadingFilters.collections}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <AutoCompleteField
              options={statusOptions}
              label="Status"
              optionLabelKey="label"
              optionIdKey="value"
              onChange={(option) => handleChange("status", option)}
              value={formik.values.status}
              disabled={searching || loadingFilters.status}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <AutoCompleteField
              options={qualityOptions}
              label="Qualidade"
              optionLabelKey="label"
              optionIdKey="id"
              onChange={(option) => handleChange("quality", option)}
              value={formik.values.quality}
              disabled={searching}
              disableClientFilter
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <AutoCompleteField
              options={commercialNames}
              label="Nome Comercial ou SKU"
              optionLabelKey="label"
              optionIdKey="id"
              onChange={(option) => handleChange("commercialName", option)}
              onInputChange={handleCommercialInputChange}
              value={formik.values.commercialName}
              disabled={searching}
              loading={loadingFilters.commercialNames}
              preserveInputWhenLoading
              disableClientFilter
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <AutoCompleteField
              options={skus}
              label="SKU"
              optionLabelKey="label"
              optionIdKey="value"
              multiple={true}
              onChange={(option) => handleChange("sku", option)}
              value={formik.values.sku}
              disabled={searching || loadingFilters.skus}
            />
          </Grid>
          {!disableImportFile && (
            <Grid item xs={12} sm={6} md={4}>
              <FormGroup width="fit-content">
                <FormLabel
                  style={{ color: themeColorsDesingSystem.text.primary }}
                >
                  Importar arquivo de SKU
                </FormLabel>
                <OutlinedInput
                  id="importFile"
                  fullWidth
                  type="file"
                  placeholder="Importar arquivo de SKU"
                  onChange={(event) =>
                    handleChange("importFile", event.target.files?.[0] ?? null)
                  }
                  accept=".csv,.xlsx,.xls,.txt"
                  endAdornment={
                    formik.values.importFile ? (
                      <InputAdornment position="end">
                        <CircularProgress
                          style={{ marginLeft: 5, marginBottom: 1 }}
                          size={16}
                        />
                      </InputAdornment>
                    ) : (
                      ""
                    )
                  }
                />
              </FormGroup>
            </Grid>
          )}
        </Grid>

        <Grid
          container
          spacing={2}
          alignItems="center"
          style={{ marginTop: "20px" }}
        >
          <Grid
            item
            xs={12}
            sm={12}
            md={8}
            style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}
          >
            <Button
              style={
                isMobile
                  ? { width: "100%", margin: "0.75rem 0" }
                  : { width: "200px", height: 56 }
              }
              color="primary"
              variant="contained"
              type="submit"
              disabled={searching}
            >
              {searching ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Consultar"
              )}
            </Button>
          </Grid>
        </Grid>
      </Container>
    </form>
  );
}
